import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StepStatus } from '../../lib/onboarding-utils';

export interface ProgressStepProps {
  label: string;
  status: StepStatus;
  index: number;
  isLast?: boolean;
  onClick?: () => void;
}

export const ProgressStep: React.FC<ProgressStepProps> = ({
  label,
  status,
  index,
  isLast,
  onClick,
}) => {
  const isCompleted = status === 'completed';
  const isCurrent = status === 'current';
  const isUpcoming = status === 'upcoming';
  const isClickable = Boolean(onClick);

  return (
    <div className="flex items-center">
      <button
        type="button"
        onClick={onClick}
        disabled={!isClickable}
        className={cn(
          'flex items-center gap-1.5 group transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md px-1',
          isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
        )}
        aria-current={isCurrent ? 'step' : undefined}
      >
        <div
          className={cn(
            'w-7 h-7 rounded-full flex items-center justify-center border-2 text-[11px] font-bold transition-colors shrink-0',
            isCompleted &&
              'bg-primary border-primary text-primary-foreground group-hover:bg-primary/80 group-hover:border-primary/80',
            isCurrent && 'border-primary text-primary ring-2 ring-primary ring-offset-1',
            isUpcoming &&
              isClickable &&
              'border-muted-foreground/40 text-muted-foreground group-hover:border-primary/60 group-hover:text-primary',
            isUpcoming && !isClickable && 'border-muted-foreground/30 text-muted-foreground/50'
          )}
        >
          {isCompleted ? <Check className="w-3.5 h-3.5 stroke-[3px]" /> : <span>{index + 1}</span>}
        </div>
        <span
          className={cn(
            'text-[11px] font-medium transition-colors whitespace-nowrap',
            isCurrent && 'text-primary',
            isCompleted && 'text-foreground group-hover:text-primary',
            isUpcoming && isClickable && 'text-muted-foreground group-hover:text-primary',
            isUpcoming && !isClickable && 'text-muted-foreground/50'
          )}
        >
          {label}
        </span>
      </button>
      {!isLast && (
        <div
          className={cn(
            'w-4 lg:w-6 h-[2px] mx-0.5 transition-colors shrink-0',
            isCompleted ? 'bg-primary' : 'bg-muted'
          )}
        />
      )}
    </div>
  );
};
